import { useState, useEffect, useCallback, useMemo } from "react";
import {
  isLabelAutoPrintDisabled,
  LABEL_AUTO_PRINT_CHANGE_EVENT,
} from "../../../services/labelAutoPrintDb";

/**
 * Hook to track label auto-print disabled status for multiple orders.
 * Returns a Set of order IDs that have auto-print disabled.
 * Automatically refreshes when preferences change.
 */
export function useLabelAutoPrintStatus(orderIds: string[]): Set<string> {
  const [disabledOrderIds, setDisabledOrderIds] = useState<Set<string>>(new Set());

  // Memoize the order IDs key for stable dependency tracking
  const orderIdsKey = useMemo(() => orderIds.join(","), [orderIds]);

  const loadStatuses = useCallback(async () => {
    if (orderIds.length === 0) {
      setDisabledOrderIds(new Set());
      return;
    }

    const disabled = new Set<string>();
    await Promise.all(
      orderIds.map(async (orderId) => {
        try {
          const isDisabled = await isLabelAutoPrintDisabled(orderId);
          if (isDisabled) {
            disabled.add(orderId);
          }
        } catch {
          // Ignore errors for individual lookups
        }
      })
    );
    setDisabledOrderIds(disabled);
  }, [orderIdsKey]);

  // Load statuses when order IDs change
  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  // Listen for changes from the toggle
  useEffect(() => {
    window.addEventListener(LABEL_AUTO_PRINT_CHANGE_EVENT, loadStatuses);
    return () => {
      window.removeEventListener(LABEL_AUTO_PRINT_CHANGE_EVENT, loadStatuses);
    };
  }, [loadStatuses]);

  return disabledOrderIds;
}
